import { type ReactNode, useId, useRef, useState } from 'react'
import type { Language } from '@eformulieren/engine'
import { type SingleProps, asText } from './kinds.js'
import { localeOf } from '../../language/text.js'

/**
 * Datum en tijdstip: `date` en `datetime`.
 *
 * **Wat er in de stand komt is wat het invoerveld geeft**: `2027-01-15` voor een
 * datum en `2027-01-15T14:30` voor een tijdstip. Dus lokale kloktijd zonder
 * zone-aanduiding, en dat is met opzet: het antwoord op "wanneer begint de
 * bijeenkomst" is een kloktijd en geen tijdstempel. Zou de control naar UTC
 * omrekenen, dan verschuift het antwoord met de zone van de invuller en klopt de
 * weergave voor de toetser niet meer.
 *
 * De evaluator gaat daar al van uit: `datumbereik` neemt het datumdeel van de
 * waarde met een reguliere uitdrukking, met het commentaar dat een `datetime` een
 * tijdstip levert en het bereik over de dag ervan gaat (engine, `controle.ts`).
 * Beide vormen zijn binnen hun soort lexicografisch vergelijkbaar.
 *
 * **Een tekstveld dd-mm-jjjj met een kalenderknop, zoals in de CitatenPOC** (`ScalarField.tsx`,
 * `nlDate.ts`; audit X-05, eisenboek 12.9). Het invoerveld van de browser toont zijn sjabloon in
 * de taal van het besturingssysteem ("dd/mm/yyyy" in een Nederlands formulier). Hier staat altijd
 * dd-mm-jjjj, en de knop opent de kalender van de browser via een verborgen datumveld. Wat in de
 * stand komt verandert niet: dezelfde ISO-vorm als hierboven.
 *
 * Het bereik zelf is een regelsoort (`datumbereik`) en geen `min`/`max` op het
 * invoerveld, om dezelfde reden als bij het getal: de melding komt uit de regel
 * (R-30) en op het moment van R-25.
 */

export interface DateProps extends SingleProps {
  readonly soort: 'date' | 'datetime'
}

export function Date({ soort: kind, koppeling: link, waarde: value, onWaarde: onValue, bewerkbaar: editable, taal: language }: DateProps): ReactNode {
  if (!editable) {
    // Wie alleen leest, krijgt de datum als datum ("15 januari 2027") en niet het invoerveld van de
    // browser met zijn sjabloon en kalenderknop (R-133). Een tekstveld en geen losse tekst, zodat de
    // vraag het label blijft en het antwoord bereikbaar is zoals bij elk alleen-lezen antwoord.
    return <input className="ef-invoer" type="text" {...link} value={readableDate(asText(value), kind, language)} readOnly />
  }
  return <EditableDate kind={kind} link={link} value={asText(value)} onValue={onValue} language={language} />
}

const TEXT = {
  sjabloon: { date: { nl: 'dd-mm-jjjj', en: 'dd-mm-yyyy' }, datetime: { nl: 'dd-mm-jjjj uu:mm', en: 'dd-mm-yyyy hh:mm' } },
  kalender: { nl: 'Kies een datum', en: 'Choose a date' },
  fout: {
    date: { nl: 'Vul een datum in als dd-mm-jjjj, bijvoorbeeld 15-01-2027.', en: 'Enter a date as dd-mm-yyyy, for example 15-01-2027.' },
    datetime: {
      nl: 'Vul een datum en tijd in als dd-mm-jjjj uu:mm, bijvoorbeeld 15-01-2027 14:30.',
      en: 'Enter a date and time as dd-mm-yyyy hh:mm, for example 15-01-2027 14:30.',
    },
  },
} as const

function EditableDate({
  kind,
  link,
  value,
  onValue,
  language,
}: {
  kind: 'date' | 'datetime'
  link: DateProps['koppeling']
  value: string
  onValue: DateProps['onWaarde']
  language: Language
}): ReactNode {
  const noticeId = useId()
  const picker = useRef<HTMLInputElement | null>(null)
  const [draft, setDraft] = useState<string | undefined>(undefined)
  const [left, setLeft] = useState(false)
  // De eigen invoer zolang die bij de waarde past; anders de waarde (die kan van een medeschrijver komen).
  const shown = draft !== undefined && (parseDate(draft, kind) ?? '') === value ? draft : value === '' ? (draft ?? '') : isoToDutch(value)
  const wrong = left && shown.trim() !== '' && parseDate(shown, kind) === undefined
  const describedBy = [link['aria-describedby'], wrong ? noticeId : undefined].filter((part) => part !== undefined).join(' ')
  return (
    <span className="ef-datum" data-soort={kind}>
      {wrong ? (
        <span className="ef-veld__melding ef-veld__melding--hard ef-datum__melding" id={noticeId}>
          {TEXT.fout[kind][language]}
        </span>
      ) : null}
      <input
        className="ef-invoer ef-datum__tekst"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        data-datum={kind}
        placeholder={TEXT.sjabloon[kind][language]}
        {...link}
        aria-describedby={describedBy === '' ? undefined : describedBy}
        {...(wrong ? { 'aria-invalid': true } : {})}
        value={shown}
        onChange={(event) => {
          const raw = event.target.value
          setDraft(raw)
          if (raw.trim() === '') {
            onValue(undefined)
            return
          }
          const parsed = parseDate(raw, kind)
          onValue(parsed)
          if (parsed !== undefined) setLeft(false)
        }}
        onBlur={() => setLeft(true)}
      />
      <button
        type="button"
        className="ef-datum__kalender"
        aria-label={TEXT.kalender[language]}
        title={TEXT.kalender[language]}
        onClick={() => {
          const element = picker.current
          if (element === null) return
          if (typeof element.showPicker === 'function') element.showPicker()
          else element.focus()
        }}
      >
        <CalendarIcon />
      </button>
      {/* Alleen de kalender van de browser; de waarde staat in het tekstveld (zoals in de POC). */}
      <input
        ref={picker}
        className="ef-datum__kiezer"
        type={kind === 'date' ? 'date' : 'datetime-local'}
        tabIndex={-1}
        aria-hidden="true"
        value={value}
        onChange={(event) => {
          const raw = event.target.value
          setDraft(undefined)
          setLeft(false)
          onValue(raw === '' ? undefined : raw)
        }}
      />
    </span>
  )
}

/** Een kalendericoon in Lucide-stijl, decoratief: de knop draagt zijn naam zelf. */
function CalendarIcon(): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  )
}

/**
 * Wat de invuller typte als ISO, of undefined. Een datum als dd-mm-jjjj (ook met / of . en een of twee
 * cijfers voor dag en maand), een tijdstip met daarachter uu:mm. Alleen een bestaande kalenderdatum.
 */
export function parseDate(raw: string, kind: 'date' | 'datetime'): string | undefined {
  const match = /^\s*(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})(?:\s+(\d{1,2}):(\d{2}))?\s*$/.exec(raw)
  if (match === null) return undefined
  const [, d = '', m = '', year = '', h, min] = match
  const day = Number(d)
  const month = Number(m)
  if (month < 1 || month > 12 || day < 1 || day > new globalThis.Date(Number(year), month, 0).getDate()) return undefined
  const date = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  if (kind === 'date') return h === undefined ? date : undefined
  if (h === undefined || min === undefined || Number(h) > 23 || Number(min) > 59) return undefined
  return `${date}T${h.padStart(2, '0')}:${min}`
}

/** Een opgeslagen ISO-datum of -tijdstip in de vorm van het tekstveld. Wat geen ISO is, blijft staan. */
function isoToDutch(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(iso)
  if (match === null) return iso
  const [, year, month, day, hour, minute] = match
  return hour === undefined ? `${day}-${month}-${year}` : `${day}-${month}-${year} ${hour}:${minute}`
}

/**
 * Een opgeslagen datum of tijdstip in de taal van de lezer. De waarde is lokale kloktijd zonder zone
 * (zie boven), dus de delen worden gelezen zoals ze er staan en niet door een `Date` met een zone
 * geschoven. Wat niet als datum te lezen is, staat er zoals het is opgeslagen.
 */
function readableDate(raw: string, kind: 'date' | 'datetime', language: Language): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/.exec(raw)
  if (match === null) return raw
  const [, year, month, day, hour, minute] = match
  const moment = new globalThis.Date(Number(year), Number(month) - 1, Number(day), Number(hour ?? 0), Number(minute ?? 0))
  const locale = localeOf(language)
  const date = moment.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })
  if (kind === 'date' || hour === undefined) return date
  return `${date}, ${moment.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`
}
